<template>
    <div>
        <table class="table">
            <caption style="border-bottom: none">Tracking</caption>
        </table>

        <div class="tracking-panel">
            <h1>Consignment tracking</h1>

            <table>
                <thead>
                    <tr>
                        <th style="width: 431px">Event</th>
                        <th style="width: 253px">Date &amp; time</th>
                        <th>Extra info</th>
                    </tr>
                </thead>

                <tbody>
                    <template v-for="(scan, index) in consignmentScans">
                        <tr :key="index">
                            <td>{{ scan.Description }}</td>
                            <td>{{ formatDate(scan.Scanned) }}</td>
                            <td rowspan="1">{{ scan.ExtraInfo }}</td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div class="tracking-panel">
            <div>
                <h1>Item tracking ({{ items.length }} items)</h1>
                <div v-if="deliveredCount > 0" class="status-pill"><information-circle /> {{ deliveredCount }} of {{ items.length }} item{{items.length === 1 ? "" : "s"}} delivered</div>
            </div>
            <div>
                <div style="position: relative">
                    <search-icon style="position: absolute; top: 50%; transform: translateY(-50%); left: 8px; pointer-events: none" />
                    <input style="margin: 0; width: 447px; padding-left: 32px" name="itemSearch" type="text" placeholder="Search by tracking number" v-model="itemSearchText" />
                </div>
                <div style="position: relative">
                    <select name="statusFilter" v-model="statusFilter" style="padding-right: 32px">
                        <option value="all">All items</option>
                        <option value="delivered">Delivered</option>
                        <option value="notDelivered">Not delivered</option>
                    </select>
                    <chevron-arrow-down style="position: absolute; top: 50%; transform: translateY(-50%); right: 8px; pointer-events: none" />
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th style="width: 32px">Item</th>
                        <th style="width: 254px">Tracking number</th>
                        <th style="width: 112px">Packaging</th>
                        <th style="width: 254px">Event</th>
                        <th style="width: 182px">Date &amp; time</th>
                        <th>Extra info</th>
                        <th style="width: 14px"></th>
                    </tr>
                </thead>

                <tbody>
                    <template v-for="(item, itemIndex) in items">
                        <template v-if="shouldShow(item)">
                            <template v-if="itemScans[item.ItemNo] && itemScans[item.ItemNo].length > 0">
                                <tr v-for="(scan, scanIndex) in visibleScans(item)">
                                    <template v-if="scanIndex === 0">
                                        <td v-bind:rowspan="visibleScans(item).length">{{ item.ItemNo }}</td>
                                        <td v-bind:rowspan="visibleScans(item).length" @click="toggleItemExpanded(item)" v-bind:class="{ clickable: itemScans[item.ItemNo].length > 1 }"><span v-html="highlight(item.ItemId)"></span></td>
                                        <td v-bind:rowspan="visibleScans(item).length">{{ item.Type }}</td>
                                    </template>
                                    <td>{{ itemScans[item.ItemNo][scanIndex].Description }}</td>
                                    <td>{{ formatDate(itemScans[item.ItemNo][scanIndex].Scanned) }}</td>
                                    <td>{{ itemScans[item.ItemNo][scanIndex].ExtraInfo }}</td>
                                    <td v-if="scanIndex === 0" v-bind:rowspan="visibleScans(item).length" @click="toggleItemExpanded(item)">
                                        <chevron-arrow-up v-if="isExpanded(item)" />
                                        <chevron-arrow-down v-else v-bind:disabled="itemScans[item.ItemNo].length <= 1" />
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr>
                                    <td>{{ item.ItemNo }}</td>
                                    <td><span v-html="highlight(item.ItemId)"></span></td>
                                    <td>{{ item.Type }}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td rowspan="1"><chevron-arrow-down disabled /></td>
                                </tr>
                            </template>
                        </template>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'

    export default {
        props: {
            items: Array,
            scans: Array,
        },

        data() {
            return {
                expandedItems: [],
                itemSearchText: "",
                statusFilter: "all"
            };
        },

        computed: {
            consignmentScans() {
                return this.scans.filter(x => x.ItemNo === 0);
            },

            itemScans() {
                return this.scans
                    .filter(x => x.ItemNo !== 0)
                    .reduce((current, scan) => {
                        let item = current[scan.ItemNo];
                        if (!item) {
                            item = [];
                            current[scan.ItemNo] = item;
                        }
                        item.push(scan);
                        return current;
                    }, {});
            },

            deliveredCount() {
                return this.items.filter(x => this.isDelivered(x)).length;
            },
        },

        methods: {
            formatDate(datetime) {
                return moment(String(datetime)).format("DD/MM/YYYY [at] h:mm A");
            },

            shouldShow(item) {
                let matchesSearchText = this.itemSearchText === "" || item.ItemId.includes(this.itemSearchText.toUpperCase());  // Presuming all tracking numbers are uppercase. We could do a case-insensitive search, but seems like there's no way to do that besides RegExp, and RegExp.escape is not widely available
                let matchesStatusFilter =
                    this.statusFilter === "all"
                    || this.statusFilter === "delivered" && this.isDelivered(item)
                    || this.statusFilter === "notDelivered" && !this.isDelivered(item);

                return matchesSearchText && matchesStatusFilter;
            },

            isDelivered(item) {
                return this.itemScans[item.ItemNo] && this.itemScans[item.ItemNo].some(x => x.ScanType === "DEL");
            },

            toggleItemExpanded(item) {
                if (this.itemScans[item.ItemNo].length <= 1) {
                    return;
                }

                let currentIndex = this.expandedItems.indexOf(item);
                if (currentIndex >= 0) {
                    this.expandedItems.splice(currentIndex, 1);
                } else {
                    this.expandedItems.push(item);
                }
            },

            isExpanded(item) {
                return this.expandedItems.indexOf(item) >= 0;
            },

            visibleScans(item) {
                let scans = this.itemScans[item.ItemNo];
                if (this.isExpanded(item)) {
                    return scans;
                } else {
                    return [scans[0]];
                }
            },

            highlight(text) {
                if (this.itemSearchText === "") {
                    return text;
                }

                let highlightedText = document.createElement("span");
                highlightedText.setAttribute("style", "background-color: #EDEAF3");
                highlightedText.appendChild(document.createTextNode(this.itemSearchText.toUpperCase()));

                return text.replace(this.itemSearchText.toUpperCase(), highlightedText.outerHTML);
            },
        }
    }
</script>

<style lang="scss" scoped>
    .tracking-panel {
        display: flex;
        padding: var(--space-m, 16px) var(--space-l, 24px) var(--space-l, 24px) var(--space-l, 24px);
        margin-bottom: 24px;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        gap: var(--space-xs, 8px);
        align-self: stretch;

        border-radius: var(--Button---Small-Corner, 4px);
        border: 1px solid #8992A0;
    }

    .tracking-panel > div {
        display: flex;
        align-items: center;
        gap: var(--space-m, 16px);
        align-self: stretch;
    }

    .tracking-panel h1 {
        width: 100%;
        margin-bottom: 0;

        color: #000;
        font-family: Inter;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 125%; /* 20px */
        letter-spacing: 0.32px;
    }

    .status-pill {
        display: flex;
        padding: var(--space-xxs, 4px) var(--space-xs, 8px);
        align-items: center;
        gap: var(--space-xxs, 4px);

        border-radius: 100px;
        border: 1px solid var(--Info-30, #CAD3EA);
        background: var(--Info-10, #ECF1F9);

        color: var(--Info-110, #2D539F);
        font-family: Inter;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px; /* 123.077% */
        white-space: nowrap;
    }

    .efm .detail table th, .efm .detail table td {
        text-align: left;
        padding: var(--space-xs, 8px);
    }

    .efm .detail table thead tr {
        border-bottom: 2px solid var(--Chrome-50, #E7E7E9);
    }

    .efm .detail table thead tr th {
        &:not(:last-child) {
            box-shadow: -1px 0 0 0 #E7E7E9 inset;
        }
    }

    .efm .detail table tbody tr td {
        box-shadow: -1px -1px 0 0 #E7E7E9 inset;

        &:last-child[rowspan] {
            box-shadow: 0 -1px 0 0 #E7E7E9 inset;
        }
    }

    .clickable {
        color: #472B88;
        cursor: pointer;
    }

    select {
        appearance: none;

        border: 1px solid #D1D0D5;
        background: #fff;
        box-sizing: border-box;
        box-shadow: inset 0px 2px 2px rgba(94, 91, 106, 0.15);
        border-radius: 4px;
        width: 253px;
        padding: 6px 4px 6px 10px;
        font-size: 16px;
        line-height: 22px;
        color: #27374E;
    }

</style>
